import React, { useEffect, useState } from "react";
import {
  getProductlist,
  updateProductlist,
  getProductEditDetails,
} from "../../../services/apiService";
import "../styles/Productlist.css";
import ReactDOM from "react-dom";
import { showSnackbar } from "../../../comopnents/snackbar";
import { Oval } from "react-loader-spinner";

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [homeSpares, setHomeSpares] = useState(false);
  const [essSpares, setEssSpares] = useState(false);
  const [newArrivals, setNewArrivals] = useState(false);
  const [loading, setLoading] = useState(false);

  // ✅ pagination states
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ✅ modal states
  const [editOpen, setEditOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const res = await getProductlist(["SuperAdmin", "Warehouse"]);
      setProducts(res.Data || []);
      setFiltered(res.Data || []);
    } catch (error) {
      console.error(error);
      showSnackbar("error", "Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Filtering
  useEffect(() => {
    let data = [...products];

    if (homeSpares) data = data.filter((p) => p.spares_home === 1);
    if (essSpares) data = data.filter((p) => p.spares_ess === 1);
    if (newArrivals) data = data.filter((p) => p.newarr === 1);

    if (search) {
      data = data.filter((p) =>
        `${p.productid} ${p.subcategory} ${p.modelno}`
          .toLowerCase()
          .includes(search.toLowerCase()),
      );
    }

    // ✅ Force Discount = 0 if NetRate > 0
    data = data.map((p) => ({
      ...p,
      discount: Number(p.netrate) > 0 ? 0 : p.discount,
    }));

    setFiltered(data);
    setPage(1);
  }, [homeSpares, essSpares, newArrivals, search, products]);

  // ✅ Pagination
  const totalItems = filtered.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const isMobile = window.innerWidth <= 768;
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = filtered.slice(startIndex, endIndex);
  const userType = localStorage.getItem("userType");

  const canEditPrice =
    userType === "SuperAdmin" ||
    userType === "Admin" ||
    userType === "AccountsManager";

  // ✅ Open modal
  const handleClose = () => {
    setEditOpen(false);
    setSelectedProduct(null);
    setForm({});
  };

  // ✅ Change input
  const onChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => {
      let updated = { ...prev, [name]: value };

      const mrp = Number(updated.mrp) || 0;
      const netrate = Number(updated.netrate) || 0;

      // ✅ Net Rate must be less than MRP
      if (name === "netrate" && netrate >= mrp && mrp > 0) {
        showSnackbar("error", "Net Rate must be less than MRP");
        updated.netrate = 0;
      }

      // ✅ Always make Discount = 0 if Net Rate > 0
      if (Number(updated.netrate) > 0) {
        updated.discount = 0;
      }

      return updated;
    });
  };

  const handleEditOpen = async (productRow) => {
    setLoading(true);
    try {
      setEditOpen(true);
      setSelectedProduct(productRow);

      const res = await getProductEditDetails(productRow.productid);
      const fullProduct = res?.Data?.[0];

      if (!fullProduct) {
        showSnackbar("error", "❌ Product details not found");
        return;
      }

      setForm({
        ...fullProduct,
        discount:
          Number(fullProduct.netrate) > 0 ? 0 : fullProduct.discount || 0,
        active: fullProduct.active || 0,
        hide: fullProduct.hide || 0,
        spares_home: fullProduct.spares_home || 0,
        spares_ess: fullProduct.spares_ess || 0,
        newarr: fullProduct.newarr || 0,
      });
    } catch (error) {
      console.error("Edit fetch error:", error.response?.data || error.message);
      showSnackbar("error", "❌ Failed to fetch product details");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Save PATCH
  const handleSave = async () => {
    try {
      setSaving(true);

      const payload = {
        ...form,
        active: Number(form.active) === 1 ? 1 : 0,
        hide: Number(form.hide) === 1 ? 1 : 0,
        spares_home: Number(form.spares_home) === 1 ? 1 : 0,
        spares_ess: Number(form.spares_ess) === 1 ? 1 : 0,
        newarr: Number(form.newarr) === 1 ? 1 : 0,
      };
      await updateProductlist(form.productid, payload);

      showSnackbar("success", "✅ Product Updated Successfully");
      handleClose();
      loadProducts();
    } catch (error) {
      console.error("Update failed:", error.response?.data || error.message);
      showSnackbar("error", "❌ Update Failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <Oval
          height={60}
          width={60}
          color="#4fa94d"
          secondaryColor="#ccc"
          strokeWidth={4}
        />
      </div>
    );
  }

  return (
    <div className="product-page">
      <div className="product-header">
        <div className="product-title">Product Details</div>

        <div className="filter-group">
          <label className="filter-toggle">
            <span className="switch">
              <input
                type="checkbox"
                checked={homeSpares}
                onChange={(e) => {
                  setHomeSpares(e.target.checked);
                  setPage(1);
                }}
              />
              <span className="slider"></span>
            </span>
            <span>Home Spares</span>
          </label>
          <label className="filter-toggle">
            <span className="switch">
              <input
                type="checkbox"
                checked={essSpares}
                onChange={(e) => {
                  setEssSpares(e.target.checked);
                  setPage(1);
                }}
              />
              <span className="slider"></span>
            </span>
            <span>Essentials Spares</span>
          </label>
          <label className="filter-toggle">
            <span className="switch">
              <input
                type="checkbox"
                checked={newArrivals}
                onChange={(e) => {
                  setNewArrivals(e.target.checked);
                  setPage(1);
                }}
              />
              <span className="slider"></span>
            </span>
            <span>New Arrivals</span>
          </label>
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by ID / Subcategory / Model No"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      {/* ✅ Table */}
      <div
        style={{
          width: isMobile ? "70vw" : "100%",
          overflowX: isMobile ? "auto" : "visible",
          WebkitOverflowScrolling: isMobile ? "touch" : "touch",
          position: "relative",
        }}
      >
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Productid</th>
                <th>Focusid</th>
                <th>Category</th>
                <th>Subcategory</th>
                <th>Brand</th>
                <th>Modelno</th>
                <th>Mrp</th>
                <th>Gst</th>
                <th>Discount</th>
                <th>MOQ</th>
                <th>Pcsqty</th>
                <th>Delminqty</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {paginatedData.length > 0 ? (
                paginatedData.map((p, index) => (
                  <tr key={`${p.productid}-${startIndex + index}`}>
                    <td style={{ color: "black" }}>{p.productid}</td>
                    <td style={{ color: "black" }}>{p.focusid}</td>
                    <td style={{ color: "black" }}>{p.category}</td>
                    <td style={{ color: "black" }}>{p.subcategory}</td>
                    <td style={{ color: "black" }}>{p.brand}</td>
                    <td style={{ color: "black" }}>{p.modelno}</td>
                    <td style={{ color: "black" }}>
                      ₹{Number(p.mrp || 0).toFixed(2)}
                    </td>
                    <td style={{ color: "black" }}>{p.gst}</td>
                    <td style={{ color: "black" }}>
                      {Number(p.netrate) > 0 ? "0%" : `${p.discount}%`}
                    </td>
                    <td style={{ color: "black" }}>{p.minorderqty}</td>
                    <td style={{ color: "black" }}>{p.pcsqty ?? "-"}</td>
                    <td style={{ color: "black" }}>{p.dealerminqty ?? "-"}</td>

                    <td style={{ color: "black" }}>
                      {p.active == 1
                        ? "Active"
                        : p.active == 0
                          ? "Inactive"
                          : "-"}
                    </td>

                    <td>
                      <button
                        className="edit-btn"
                        onClick={() => handleEditOpen(p)}
                      >
                        ✏️
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="14"
                    style={{ textAlign: "center", padding: "40px" }}
                  >
                    No products found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* ✅ Pagination UI */}
          <div className="pagination-container">
            <div style={{ color: "black" }}>
              Items per page:
              <select
                className="page-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {[5, 10, 20, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            <div className="page-buttons">
              <button
                className="page-btn"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </button>

              <span className="page-count">
                {page} / {totalPages || 1}
              </span>

              <button
                className="page-btn"
                disabled={page === totalPages || totalPages === 0}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ EDIT MODAL */}
      {editOpen &&
        ReactDOM.createPortal(
          <div className="editModalOverlay">
            <div className="editModalBox">
              <div className="editModalHeader">
                <h3>Edit Product</h3>
                <button className="closeBtn" onClick={handleClose}>
                  ✕
                </button>
              </div>

              <div className="editModalBody">
                <div className="gridForm">
                  <div>
                    <label>Product ID</label>
                    <input value={form.productid} disabled />
                  </div>
                  <div>
                    <label>Client Id</label>
                    <input
                      name="clientid"
                      value={form.clientid}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>Pack Qty</label>
                    <input
                      name="packqty"
                      value={form.packqty}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>Carton Qty</label>
                    <input
                      name="cartqty"
                      value={form.cartqty}
                      onChange={onChange}
                    />
                  </div>

                  <div>
                    <label>Model No</label>
                    <input
                      name="modelno"
                      value={form.modelno}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>Focus ID</label>
                    <input
                      name="focusid"
                      value={form.focusid}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>Category</label>
                    <input
                      name="category"
                      value={form.category}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>Sub Category</label>
                    <input
                      name="subcategory"
                      value={form.subcategory}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>Brand</label>
                    <input
                      name="brand"
                      value={form.brand}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>Barcode</label>
                    <input
                      name="barcode"
                      value={form.barcode}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>SKU</label>
                    <input name="sku" value={form.sku} onChange={onChange} />
                  </div>
                  <div>
                    <label>Price (MRP)</label>
                    <input
                      name="mrp"
                      value={form.mrp}
                      onChange={onChange}
                      disabled={!canEditPrice}
                    />
                  </div>
                  <div>
                    <label>GST</label>
                    <input
                      name="gst"
                      value={form.gst}
                      onChange={onChange}
                      disabled={!canEditPrice}
                    />
                  </div>
                  <div>
                    <label>Discount</label>
                    <input
                      name="discount"
                      value={form.discount}
                      onChange={onChange}
                      disabled={Number(form.netrate) > 0 || !canEditPrice}
                    />
                  </div>
                  <div>
                    <label>Dealer Discount</label>
                    <input
                      name="delardiscount"
                      value={Number(form.netrate) > 0 ? 0 : form.delardiscount}
                      onChange={onChange}
                      disabled={Number(form.netrate) > 0 || !canEditPrice}
                    />
                  </div>
                  <div>
                    <label>Mapping Id</label>
                    <input
                      name="mappingid"
                      value={form.mappingid}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>Net Rate</label>
                    <input
                      name="netrate"
                      value={form.netrate}
                      onChange={onChange}
                      disabled={!canEditPrice}
                    />
                  </div>
                  <div>
                    <label>Position</label>
                    <input
                      name="position"
                      value={form.position}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>Part No</label>
                    <input
                      name="partno"
                      value={form.partno}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>Min-Order</label>
                    <input
                      name="minorderqty"
                      value={form.minorderqty}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>Dealer Min-Order qty</label>
                    <input
                      name="dealerminqty"
                      value={form.dealerminqty}
                      onChange={onChange}
                      disabled={!canEditPrice}
                    />
                  </div>
                  <div>
                    <label>Pcs-Qty</label>
                    <input
                      name="pcsqty"
                      value={form.pcsqty}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>Keywords</label>
                    <textarea
                      name="keywords"
                      value={form.keywords}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>Image1</label>
                    <input
                      name="image1"
                      value={form.image1}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>Image2</label>
                    <input
                      name="image2"
                      value={form.image2}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>Image3</label>
                    <input
                      name="image3"
                      value={form.image3}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>Image4</label>
                    <input
                      name="image4"
                      value={form.image4}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>explore Image</label>
                    <input
                      name="exploreimage"
                      value={form.exploreimage}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>specifications Image</label>
                    <input
                      name="spec_image"
                      value={form.spec_image}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>Video</label>
                    <input
                      name="video"
                      value={form.video}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>Description</label>
                    <textarea
                      name="description"
                      value={form.description}
                      onChange={onChange}
                    />
                  </div>

                  <div>
                    <label>Metatags</label>
                    <input
                      name="metatags"
                      value={form.metatags}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>Profession</label>
                    <input
                      name="profession"
                      value={form.profession}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>NewArrivals</label>
                    <input
                      name="newarr"
                      value={form.newarr}
                      onChange={onChange}
                    />
                  </div>

                  <div>
                    <label>Min Stock qty</label>
                    <input
                      name="stockminqty"
                      value={form.stockminqty}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>Gross w</label>
                    <input
                      name="grossw"
                      value={form.grossw}
                      onChange={onChange}
                    />
                  </div>
                  <div>
                    <label>Net w</label>
                    <input name="netw" value={form.netw} onChange={onChange} />
                  </div>
                  <div>
                    <label>Cart W</label>
                    <input
                      name="cartw"
                      value={form.cartw}
                      onChange={onChange}
                    />
                  </div>
                    <div>
                    <label>pack qty</label>
                    <input
                      name="packqty"
                      value={form.packqty}
                      onChange={onChange}
                    />
                  </div>
                    <div>
                    <label>cart qty</label>
                    <input
                      name="cartqty"
                      value={form.cartqty}
                      onChange={onChange}
                    />
                  </div>
                    <div>
                    <label>packdis </label>
                    <input
                      name="packdis"
                      value={form.packdis}
                      onChange={onChange}
                    />
                  </div>
                       <div>
                    <label>cartdis </label>
                    <input
                      name="cartdis"
                      value={form.cartdis}
                      onChange={onChange}
                    />
                  </div>
                      <div>
                    <label>warrent_type </label>
                    <input
                      name="warrent_type"
                      value={form.warrent_type}
                      onChange={onChange}
                    />
                  </div>
                </div>

                <div className="bottomSwitches">
                  <label>
                    <input
                      type="checkbox"
                      checked={Number(form.active) === 1}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          active: e.target.checked ? 1 : 0,
                        }))
                      }
                    />
                    Is Active
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      checked={Number(form.hide) === 1}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          hide: e.target.checked ? 1 : 0,
                        }))
                      }
                    />
                    Is Hide
                  </label>
                </div>
              </div>

              <div className="editModalFooter">
                <button
                  className="saveBtn"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <Oval height={20} width={20} color="#fff" />
                  ) : (
                    "Save changes"
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ProductList;
